import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

export const ENTITY_LABELS = { mock_exam: "Mock Exam", paper: "Past Paper", worksheet: "Worksheet" };

/**
 * Deletes everything a generation job produced: the generated question bank questions,
 * and optionally the Mock Exam / Past Paper / Worksheet created from them. Shared by the
 * MCQ conversion and Similar Paper review pages.
 *
 * Props:
 *   open, job, busy, onClose
 *   onConfirm({ delete_entities, delete_job })
 *   nounSingular   — "MCQ" | "generated question" (used in the title/body)
 *   listLabel      — "MCQ Conversions" | "Similar Papers" (the list the job row lives in)
 *   regenerateHint — "Convert to MCQ" | "Generate Similar Paper" (how to redo it)
 */
const JobDeleteGeneratedDialog = ({
  open,
  job,
  busy,
  onClose,
  onConfirm,
  nounSingular = "generated question",
  listLabel = "list",
  regenerateHint = "regenerate",
}) => {
  const [deleteEntities, setDeleteEntities] = useState(true);
  const [deleteJob, setDeleteJob] = useState(false);

  useEffect(() => {
    if (open) {
      setDeleteEntities(true);
      setDeleteJob(false);
    }
  }, [open]);

  const entities = job?.created_entities || [];
  const savedCount = (job?.imported_question_ids || []).length;

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Delete {nounSingular}s?</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This permanently removes every question bank question this job created, along with
          their options and answers
          {savedCount > 0 ? ` (${savedCount} saved)` : ""}. The original imported questions are
          not affected. You can then regenerate from scratch.
        </Typography>

        {entities.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              This job was also saved as:
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 0.5 }}>
              {entities.map((e) => (
                <Chip
                  key={`${e.entity_type}-${e.entity_id}`}
                  size="small"
                  label={`${ENTITY_LABELS[e.entity_type] || e.entity_type}: ${e.name}`}
                />
              ))}
            </Stack>
          </Alert>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={deleteEntities}
              disabled={busy || entities.length === 0}
              onChange={(e) => setDeleteEntities(e.target.checked)}
            />
          }
          label={
            entities.length === 0
              ? "No Mock Exam / Past Paper / Worksheet was created from this job"
              : "Also delete the created Mock Exam / Past Paper / Worksheet"
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={deleteJob}
              disabled={busy}
              onChange={(e) => setDeleteJob(e.target.checked)}
            />
          }
          label={`Also remove this job from the ${listLabel} list`}
        />
        {deleteJob && (
          <Alert severity="info" sx={{ mt: 1 }}>
            The generated questions are discarded with the job record — you would need to run
            &ldquo;{regenerateHint}&rdquo; on the paper again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          onClick={() => onConfirm({ delete_entities: deleteEntities, delete_job: deleteJob })}
        >
          {busy ? "Deleting…" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobDeleteGeneratedDialog;
